/**
 * Thin, stateless wrapper around the browser Fullscreen API (#967). Checks the
 * unprefixed surface first and falls back to Safari's legacy `webkit`-prefixed
 * one (unprefixed only since Safari 16.4) — every operation degrades the same
 * way rather than assuming a specific Safari version. Deliberately stateless:
 * callers re-derive the current state via `isFullscreenActive()` on every
 * `fullscreenchange`, rather than this module tracking its own copy — the DOM
 * is the single source of truth, which is what lets the caller's UI resync
 * correctly after an exit it did not itself trigger (Esc, OS shortcut,
 * traffic-light button).
 */

interface PrefixedDocument extends Document {
    webkitFullscreenEnabled?: boolean;
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void> | void;
}

interface PrefixedElement extends HTMLElement {
    webkitRequestFullscreen?: () => Promise<void> | void;
}

/** Whether the Fullscreen API is available at all (false on e.g. iOS Safari). */
export function isFullscreenSupported(): boolean {
    const doc = document as PrefixedDocument;
    return Boolean(doc.fullscreenEnabled ?? doc.webkitFullscreenEnabled ?? false);
}

/** Whether some element is currently the fullscreen element. */
export function isFullscreenActive(): boolean {
    const doc = document as PrefixedDocument;
    return Boolean(doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null);
}

/** Request fullscreen on `el`, using the webkit-prefixed method if the unprefixed one is absent. */
export async function requestFullscreen(el: HTMLElement): Promise<void> {
    const target = el as PrefixedElement;
    if (target.requestFullscreen) {
        await target.requestFullscreen();
    } else if (target.webkitRequestFullscreen) {
        await target.webkitRequestFullscreen();
    }
}

/** Exit fullscreen, using the webkit-prefixed method if the unprefixed one is absent. */
export async function exitFullscreen(): Promise<void> {
    const doc = document as PrefixedDocument;
    if (doc.exitFullscreen) {
        await doc.exitFullscreen();
    } else if (doc.webkitExitFullscreen) {
        await doc.webkitExitFullscreen();
    }
}

/**
 * Subscribe to fullscreen changes on both the unprefixed and webkit-prefixed
 * event names (only one will ever actually fire in a given browser). Returns
 * an unsubscribe function removing both listeners.
 */
export function onFullscreenChange(handler: () => void): () => void {
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
    return () => {
        document.removeEventListener('fullscreenchange', handler);
        document.removeEventListener('webkitfullscreenchange', handler);
    };
}
